
import React, { useEffect, useMemo, useState } from 'react';
import { NSNavTabs } from 'bricks';
import { TabContent } from 'reactstrap';
import RenderIf from 'components/RenderIf/RenderIf';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { isEmpty } from 'lodash';
import { useAuthContext } from 'contexts/AuthContext';
import { getSaleIndexForCadence } from 'views/ProFormaTable/helpers';
import TopLevelAnalysis from './TopLevelAnalysis/TopLevelAnalysis';
import ProFormaGenericTable from '../../ProFormaGenericTable/ProFormaGenericTable';
import {
    WATERFALL_SUMMARY_COLUMNS,
    WATERFALL_TABS,
    GP_EXPANDED,
    LP_EXPANDED,
    OVERVIEW,
    transformContributionsRows,
    transformDistributionWaterfallRows,
    transformDistributionRowsCashFlow,
    transformNetCashFlowsRows,
    transformWaterfallSummaryRows,
    transformGpContributionsRows,
    transformGpDistributionWaterfallRows,
    transformGpDistributionRowsCashFlow,
    transformGpNetCashFlowsRows,
} from './constants';
import './WaterfallAnalysis.scss';
import ContributionsAndDistributions from './ContributionsAndDistributions/ContributionsAndDistributions';
import { useProFormaSocketContext } from '../../ProForma/socketContext/ProFormaSocketProvider';
import { getContributionsTotals } from './ContributionsAndDistributions/constants';

export interface IWaterfallAnalysisProps {
    hasWriteAccessToProForma: boolean;
}

const WaterfallAnalysis = ({ hasWriteAccessToProForma }: IWaterfallAnalysisProps) => {
    const {
        waterfallAnalysis,
        handleUpdateDistributionWaterfall,
        handleUpdateProFormaPartners,
        proForma,
        cashFlowPeriodsByCadence,
        periodCadence,
        operatingInfo,
    } = useProFormaSocketContext();
    const { user } = useAuthContext();
    const isNorthspyreUser = user?.email?.toLowerCase().endsWith('@northspyre.com');
    const [activeTabName, setActiveTabName] = useState<string>(OVERVIEW);
    const [tabsDisplay, setTabsDisplay] = useState({ isSingleLp: true, isSingleGp: true });

    useEffect(() => {
        if (waterfallAnalysis && waterfallAnalysis.isSingleLp !== undefined && waterfallAnalysis.isSingleGp !== undefined) {
            setTabsDisplay({ isSingleLp: waterfallAnalysis?.isSingleLp, isSingleGp: waterfallAnalysis?.isSingleGp });
        }
    }, [waterfallAnalysis]);

    const saleIndex = getSaleIndexForCadence(operatingInfo.saleMonth, periodCadence);

    const waterfallType = !waterfallAnalysis.isSingleGp && activeTabName === GP_EXPANDED ? 'multi_gp' : 'basic';

    const contributionsRowsPopulated = React.useMemo(
        () => transformContributionsRows(
            waterfallAnalysis?.gp,
            waterfallAnalysis?.lp,
            waterfallAnalysis?.contributions,
            waterfallAnalysis?.contributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        ),
        [
            waterfallAnalysis?.gp,
            waterfallAnalysis?.lp,
            waterfallAnalysis?.contributions,
            waterfallAnalysis?.contributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        ],
    );

    const gpContributionsRowsPopulated = React.useMemo(
        () => transformGpContributionsRows(
            waterfallAnalysis?.gp,
            waterfallAnalysis?.gpGpContributions,
            waterfallAnalysis?.gpGpContributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        ),
        [
            waterfallAnalysis?.gp,
            waterfallAnalysis?.gpGpContributions,
            waterfallAnalysis?.gpGpContributionsCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        ],
    );

    const distributionWaterfallRowsPopulated = React.useMemo(
        () => transformDistributionWaterfallRows(
            waterfallAnalysis?.distributionWaterfall,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.distributionWaterfallCashFlow,
            saleIndex,
        ),
        [
            waterfallAnalysis?.distributionWaterfall,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.distributionWaterfallCashFlow,
            saleIndex,
        ],
    );

    const gpDistributionWaterfallRowsPopulated = React.useMemo(
        () => transformGpDistributionWaterfallRows(
            waterfallAnalysis?.gpGpDistributionWaterfall,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpDistributionWaterfallCashFlow,
            saleIndex,
        ),
        [
            waterfallAnalysis?.gpGpDistributionWaterfall,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpDistributionWaterfallCashFlow,
            saleIndex,
        ],
    );

    const distributionsRowsPopulated = React.useMemo(
        () => transformDistributionRowsCashFlow(
            waterfallAnalysis?.distributions,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.distributionsCashFlow,
            saleIndex,
        ),
        [
            waterfallAnalysis?.distributions,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.distributionsCashFlow,
            saleIndex,
        ],
    );

    const gpDistributionRowsPopulated = React.useMemo(
        () => transformGpDistributionRowsCashFlow(
            waterfallAnalysis?.gpGpDistributions,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpDistributionsCashFlow,
            saleIndex,
        ),
        [
            waterfallAnalysis?.gpGpDistributions,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpDistributionsCashFlow,
            saleIndex,
        ],
    );

    const netCashFlowRowsPopulated = React.useMemo(
        () => transformNetCashFlowsRows(
            waterfallAnalysis?.netCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.netCashFlowCashFlow,
            saleIndex,
        ),
        [
            waterfallAnalysis?.netCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.netCashFlowCashFlow,
            saleIndex,
        ],
    );

    const gpNetCashFlowRowsPopulated = React.useMemo(
        () => transformGpNetCashFlowsRows(
            waterfallAnalysis?.gpGpNetCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpNetCashFlowCashFlow,
            saleIndex,
        ),
        [
            waterfallAnalysis?.gpGpNetCashFlow,
            proForma,
            cashFlowPeriodsByCadence,
            periodCadence,
            waterfallAnalysis?.gpGpNetCashFlowCashFlow,
            saleIndex,
        ],
    );

    const waterfallSummaryRows = React.useMemo(
        () => transformWaterfallSummaryRows(tabsDisplay.isSingleGp, waterfallAnalysis?.waterfallSummary),
        [waterfallAnalysis?.waterfallSummary, tabsDisplay],
    );

    const handleTabsDisplayChange = (key: string, value: boolean) => {
        setTabsDisplay(prev => ({ ...prev, [key]: value }));
    };

    const tabs = WATERFALL_TABS.filter(tab => (
        (!tabsDisplay.isSingleLp && tab === LP_EXPANDED) ||
        (!tabsDisplay.isSingleGp && tab === GP_EXPANDED && isNorthspyreUser) ||
        ((!tabsDisplay.isSingleLp || !tabsDisplay.isSingleGp) && tab === OVERVIEW)
    ));

    const updateDistributionWaterfallRow = (rowId: number, dataKey: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        const updateMap: Record<number, Record<string, Record<string, number>>> = {
            0: {
                upTo: { preferredReturnHurdleRate: value },
            },
            1: {
                upTo: { secondTierHurdleRate: value },
                gpPromote: { secondTierGpPromote: value },
            },
            2: {
                upTo: { thirdTierHurdleRate: value },
                gpPromote: { thirdTierGpPromote: value },
            },
            3: {
                gpPromote: { fourthTierGpPromote: value },
            },
        };

        const updateData = updateMap[rowId]?.[dataKey];
        if (updateData) {
            handleUpdateDistributionWaterfall(waterfallType, updateData);
        }
    };

    const updateContributionRow = (rowId: number, dataKey: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = dataKey === 'share'
            ? (parseFloat(e.target.value)).toString()
            : e.target.value;

        const updateData = {
            id: rowId,
            [dataKey === 'share' ? 'contributionShare' : 'name']: value,
        };

        handleUpdateProFormaPartners(updateData);
    };

    const contributionsTotalsWithPeriods = useMemo(() => {
        const contributionsTotals = activeTabName === OVERVIEW ? {
            amount: waterfallAnalysis?.contributions?.totalAmount,
            share: waterfallAnalysis?.contributions?.totalSharePercent,
        } : {
            amount: waterfallAnalysis?.gpGpContributions?.totalEquityAmount,
            share: waterfallAnalysis?.gpGpContributions?.totalShareOfDevFee,
        };

        const totals = getContributionsTotals(
            activeTabName === OVERVIEW ? waterfallAnalysis.contributionsCashFlow : waterfallAnalysis.gpGpContributionsCashFlow,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        );
        return {
            ...contributionsTotals,
            ...totals,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waterfallAnalysis?.contributionsCashFlow, cashFlowPeriodsByCadence, periodCadence, activeTabName, saleIndex]);

    const distributionTotalsWithPeriods = useMemo(() => {
        const distributionTotals = activeTabName === OVERVIEW ? {
            amount: waterfallAnalysis?.distributions?.totalAmount,
            share: waterfallAnalysis?.distributions?.totalShare,
        } : {
            amount: waterfallAnalysis?.gpGpDistributions?.totalAmount,
            share: waterfallAnalysis?.gpGpDistributions?.totalShare,
        };

        const totals = getContributionsTotals(
            activeTabName === OVERVIEW ? waterfallAnalysis.distributionsCashFlow : waterfallAnalysis.gpGpDistributionsCashFlow,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        );
        return {
            ...distributionTotals,
            ...totals,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waterfallAnalysis?.distributionsCashFlow, cashFlowPeriodsByCadence, periodCadence, activeTabName, saleIndex]);

    const netCashFlowTotalsWithPeriods = useMemo(() => {
        const netCashFlowTotals = activeTabName === OVERVIEW ? {
            amount: waterfallAnalysis?.netCashFlow?.totalAmount,
            share: waterfallAnalysis?.netCashFlow?.totalShare,
            emx: waterfallAnalysis?.netCashFlow?.totalEmx,
            irr: waterfallAnalysis?.netCashFlow?.totalIrr,
        } : {
            amount: waterfallAnalysis?.gpGpNetCashFlow?.totalAmount,
            share: waterfallAnalysis?.gpGpNetCashFlow?.totalShare,
            emx: waterfallAnalysis?.gpGpNetCashFlow?.totalEmx,
            irr: waterfallAnalysis?.gpGpNetCashFlow?.totalIrr,
        };

        const totals = getContributionsTotals(
            activeTabName === OVERVIEW ? waterfallAnalysis.netCashFlowCashFlow : waterfallAnalysis.gpGpNetCashFlowCashFlow,
            cashFlowPeriodsByCadence,
            periodCadence,
            saleIndex,
        );
        return {
            ...netCashFlowTotals,
            ...totals,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waterfallAnalysis?.netCashFlow, cashFlowPeriodsByCadence, periodCadence, activeTabName, saleIndex]);

    const BasicTables = (
        <ContributionsAndDistributions
            waterfallType={waterfallType}
            contributionsRows={contributionsRowsPopulated}
            distributionWaterfallRows={distributionWaterfallRowsPopulated}
            updateDistributionWaterfallRow={updateDistributionWaterfallRow}
            updateContributionRow={updateContributionRow}
            distributionsRows={distributionsRowsPopulated}
            netCashFlowRows={netCashFlowRowsPopulated}
            contributionsTotals={contributionsTotalsWithPeriods}
            distributionsTotals={distributionTotalsWithPeriods}
            netCashFlowTotals={netCashFlowTotalsWithPeriods}
        />
    );

    return (
        <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma}>
            <RenderIf isTrue={!isEmpty(proForma)} isLoading={isEmpty(proForma)}>
                <div className="WaterfallAnalysis">
                    <TopLevelAnalysis onChange={handleTabsDisplayChange} />
                    <div className="WaterfallAnalysis__separator mx-3 mb-3" />
                    <div className="w-100 px-3 pb-3">
                        <ProFormaGenericTable
                            fixedColumnsMaxWidth="100%"
                            columns={WATERFALL_SUMMARY_COLUMNS}
                            rows={waterfallSummaryRows}
                            setRows={() => { }}
                            hideAddButton
                            hideTotalFooter
                            hideCashFlowTotals
                        />
                    </div>
                    <div className="WaterfallAnalysis__separator mx-3 mb-3" />
                    <div className="w-100 px-3 pb-3">
                        <RenderIf isTrue={tabsDisplay.isSingleGp && tabsDisplay.isSingleLp}>
                            {BasicTables}
                        </RenderIf>
                        <RenderIf isTrue={!tabsDisplay.isSingleLp || !tabsDisplay.isSingleGp}>
                            <NSNavTabs
                                containerClassName="d-flex mt-2"
                                navItems={tabs.map((navName, index) => ({
                                    callback: () => setActiveTabName(navName),
                                    name: navName,
                                    isActive: activeTabName === navName,
                                    id: `watefall-analysis-${index}`,
                                    className: 'WaterfallAnalysis__tabs text-dark text-nowrap px-4 mr-2',
                                }))}
                            />

                            <TabContent activeTab={activeTabName} className="NSCard--level-3 d-flex w-100 h-100 pl-3 pt-3 pb-3">
                                <RenderIf isTrue={activeTabName === OVERVIEW}>
                                    {BasicTables}
                                </RenderIf>
                                <RenderIf isTrue={activeTabName === GP_EXPANDED}>
                                    <ContributionsAndDistributions
                                        waterfallType={waterfallType}
                                        contributionsRows={gpContributionsRowsPopulated}
                                        distributionWaterfallRows={gpDistributionWaterfallRowsPopulated}
                                        updateDistributionWaterfallRow={updateDistributionWaterfallRow}
                                        updateContributionRow={updateContributionRow}
                                        distributionsRows={gpDistributionRowsPopulated}
                                        netCashFlowRows={gpNetCashFlowRowsPopulated}
                                        distributionsTotals={distributionTotalsWithPeriods}
                                        netCashFlowTotals={netCashFlowTotalsWithPeriods}
                                        contributionsTotals={contributionsTotalsWithPeriods}
                                    />
                                </RenderIf>
                            </TabContent>
                        </RenderIf>
                    </div>
                </div>
            </RenderIf>
        </ReadOnlyWrapper>
    );
};

export default WaterfallAnalysis;
